// ABOUTME: Grid filter with a from/to number input, writing { min, max } under its filter key.
// ABOUTME: General purpose — the field it filters is decided by the filter key of the caller.

import { Box, InputBase, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useGridFilter } from "../../../../../contexts/GridFilterContext";
import { updateFilter } from "../../../../../newapi/global/filter/gridFilterSlice";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";

/**
 * params:
 * - key: the filter key the range is stored under, which the backend reads as { min, max }
 * - step / min: passed to the inputs; step defaults to 1, so whole numbers only
 * - allowNegative: drops the lower limit, for a value that can run below zero such as a saldo
 * - unit: optional suffix shown behind the inputs, e.g. "g"
 */
const GeneralNumberRangeFilter = ({ params }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const filterKey = params.key;
    const { index, id } = useGridFilter();
    const filterObject = useSelector((state) => {
        const instance = state.gridFilter.viewInstances[index];
        return instance ? instance[id] : undefined;
    });

    const range = filterObject?.filter?.[filterKey] ?? {};

    const updateBound = (bound, value) => {
        if (!filterObject || !filterObject.filter) {
            return;
        }

        const next = { ...range, [bound]: value };

        // An empty pair is dropped rather than stored, so a cleared filter stops narrowing
        // the grid instead of sending two blanks along with every request.
        const isEmpty = (next.min === "" || next.min === undefined || next.min === null)
            && (next.max === "" || next.max === undefined || next.max === null);

        dispatch(updateFilter({ index, id, filterKey, value: isEmpty ? null : next }));
    };

    // A weight cannot be negative and the inputs say so, but a saldo can: the whole point of
    // filtering on it is to find the orders that ran into the red.
    const inputProps = {
        step: params.step ?? 1,
        ...(params.allowNegative ? {} : { min: params.min ?? 0 })
    };

    const inputSx = {
        flex: 1,
        px: 2,
        height: 32,
        borderRadius: 4,
        color: colors.txt["primary"],
        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
        "& input": {
            padding: 0,
            lineHeight: "32px",
            // The spinners crowd an input this size and the value is typed anyway.
            "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0
            },
            MozAppearance: "textfield"
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <InputBase
                    sx={inputSx}
                    type="number"
                    inputProps={inputProps}
                    value={range.min ?? ""}
                    onChange={(e) => updateBound("min", e.target.value)}
                    placeholder={t("from")}
                />
                <Text light>-</Text>
                <InputBase
                    sx={inputSx}
                    type="number"
                    inputProps={inputProps}
                    value={range.max ?? ""}
                    onChange={(e) => updateBound("max", e.target.value)}
                    placeholder={t("to")}
                />
                {params.unit && <Text light>{params.unit}</Text>}
            </Box>
        </Box>
    );
};

export default GeneralNumberRangeFilter;
